// Google Drive picker — lets an admin pick an existing image from their Drive
// or upload a new one straight to Drive, entirely from the browser. This is
// deliberately NOT routed through our backend/nginx: the whole point is to
// bypass our server's upload path (and its body-size limit) for large images.
// Only the resulting small JSON {imageUrl} is ever sent to our API.
//
// Scope used is 'drive.file' — the app can only see/manage files it created
// or that the user explicitly picked with this picker, never the rest of the
// user's Drive.

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const SCOPE = 'https://www.googleapis.com/auth/drive.file';

let gisLoaded = null;
let gapiLoaded = null;
let tokenClient = null;
let cachedToken = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

function ensureScripts() {
  if (!gisLoaded) gisLoaded = loadScript('https://accounts.google.com/gsi/client');
  if (!gapiLoaded) gapiLoaded = loadScript('https://apis.google.com/js/api.js').then(
    () => new Promise((resolve) => window.gapi.load('picker', resolve))
  );
  return Promise.all([gisLoaded, gapiLoaded]);
}

export function isGoogleDriveConfigured() {
  return Boolean(CLIENT_ID && API_KEY);
}

async function getAccessToken() {
  if (!isGoogleDriveConfigured()) {
    throw new Error('Google Drive is not configured — set REACT_APP_GOOGLE_CLIENT_ID / REACT_APP_GOOGLE_API_KEY');
  }
  await ensureScripts();
  if (cachedToken) return cachedToken;

  return new Promise((resolve, reject) => {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPE,
      callback: (resp) => {
        if (resp.error) return reject(new Error(resp.error));
        cachedToken = resp.access_token;
        resolve(cachedToken);
      },
      error_callback: (err) => reject(new Error(err.message || 'Google sign-in failed or was cancelled')),
    });
    tokenClient.requestAccessToken({ prompt: cachedToken ? '' : 'consent' });
  });
}

function openPicker(accessToken) {
  return new Promise((resolve, reject) => {
    const uploadView = new window.google.picker.DocsUploadView();
    const imagesView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS_IMAGES)
      .setIncludeFolders(true);

    const picker = new window.google.picker.PickerBuilder()
      .addView(uploadView)
      .addView(imagesView)
      .setOAuthToken(accessToken)
      .setDeveloperKey(API_KEY)
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          resolve(data.docs[0]);
        } else if (data.action === window.google.picker.Action.CANCEL) {
          reject(new Error('cancelled'));
        }
      })
      .build();
    picker.setVisible(true);
  });
}

// Files picked/uploaded via the picker are private by default — make this one
// viewable by anyone with the link so it can be used as a plain <img src>.
async function makePublic(fileId, accessToken) {
  await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}/permissions`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role: 'reader', type: 'anyone' }),
  });
  return `https://lh3.googleusercontent.com/d/${fileId}`;
}

// Full flow: sign in (if needed) -> open picker -> make the pick public -> return its image URL.
// Throws with message 'cancelled' if the user closes the picker without choosing anything.
export async function pickOrUploadDriveImage() {
  const accessToken = await getAccessToken();
  const doc = await openPicker(accessToken);
  const imageUrl = await makePublic(doc.id, accessToken);
  return { fileId: doc.id, name: doc.name, imageUrl };
}
